import { z } from "zod/v4";
import { invoiceDetailResponseSchema, invoiceResponseSchema } from "../../schemas/finance";
import { fetchApi, fetchVoid } from "../client/internal";

const paginatedInvoices = z.object({
	items: z.array(invoiceResponseSchema),
	total: z.number(),
	limit: z.number(),
	offset: z.number(),
});

export type InvoiceListParams = {
	limit?: number;
	offset?: number;
};

export const invoices = {
	list: (clubId: string, status?: string, params: InvoiceListParams = {}) => {
		const searchParams = new URLSearchParams();
		if (status) searchParams.set("status", status);
		if (params.limit !== undefined) searchParams.set("limit", String(params.limit));
		if (params.offset !== undefined) searchParams.set("offset", String(params.offset));
		const qs = searchParams.toString();
		return fetchApi(`/${clubId}/invoices${qs ? `?${qs}` : ""}`, paginatedInvoices);
	},
	listByMember: (clubId: string, memberId: string) =>
		fetchApi(`/${clubId}/invoices?member_id=${memberId}`, z.array(invoiceResponseSchema)),
	get: (clubId: string, invoiceId: string) =>
		fetchApi(`/${clubId}/invoices/${invoiceId}`, invoiceDetailResponseSchema),
	create: (clubId: string, data: unknown) =>
		fetchApi(`/${clubId}/invoices`, invoiceDetailResponseSchema, {
			method: "POST",
			body: JSON.stringify(data),
		}),
	update: (clubId: string, invoiceId: string, data: unknown) =>
		fetchApi(`/${clubId}/invoices/${invoiceId}`, invoiceDetailResponseSchema, {
			method: "PUT",
			body: JSON.stringify(data),
		}),
	delete: (clubId: string, invoiceId: string) =>
		fetchVoid(`/${clubId}/invoices/${invoiceId}`, { method: "DELETE" }),
	transition: (
		clubId: string,
		invoiceId: string,
		data: { status: string; amount_cents?: number; method?: string },
	) =>
		fetchApi(`/${clubId}/invoices/${invoiceId}`, invoiceDetailResponseSchema, {
			method: "PATCH",
			body: JSON.stringify(data),
		}),
	generate: (clubId: string, data: { fee_id: string; member_ids?: string[]; due_date: string }) =>
		fetchApi(
			`/${clubId}/invoices/generate`,
			z.object({
				created: z.number(),
				skipped: z.number(),
				total: z.number(),
			}),
			{
				method: "POST",
				body: JSON.stringify(data),
			},
		),
	creditNotes: {
		list: (clubId: string, invoiceId: string) =>
			fetchApi(
				`/${clubId}/invoices/${invoiceId}/credit-notes`,
				z.array(
					z.object({
						id: z.string(),
						invoice_id: z.string(),
						club_id: z.string(),
						reason: z.string(),
						amount_cents: z.number(),
						is_refund: z.boolean(),
						stripe_refund_id: z.string().nullable().optional(),
						created_at: z.coerce.date(),
					}),
				),
			),
		create: (
			clubId: string,
			invoiceId: string,
			data: { reason: string; amount_cents: number; is_refund: boolean },
		) =>
			fetchApi(
				`/${clubId}/invoices/${invoiceId}/credit-notes`,
				z.object({
					id: z.string(),
					invoice_id: z.string(),
					club_id: z.string(),
					reason: z.string(),
					amount_cents: z.number(),
					currency: z.string(),
					is_refund: z.boolean(),
					stripe_refund_id: z.string().nullable().optional(),
					created_at: z.coerce.date(),
				}),
				{
					method: "POST",
					body: JSON.stringify(data),
				},
			),
	},
};
